import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Send, X, Info, Clock } from 'lucide-react';
import { useToast, AsyncBoundary } from '@kollez/design-system';
import { useOpportunity, useCreateOpportunity, useUpdateOpportunity, usePublishOpportunity, useWorkflows, usePartnerColleges } from '@/lib/queries';
import { SkillPicker } from '@/components/SkillPicker';
import { formatLPA } from '@kollez/shared';
import { OPPORTUNITY_TYPES, OPPORTUNITY_TYPE_LABELS, type OpportunityType } from '@kollez/shared/enums';

/**
 * /company/openings/new · /company/openings/:id (F89a) — direct port of
 * kollez/pages/company/opening.html.
 *   Left form: Role · Pay (CTC-row list) · Who-can-apply (branch tags + CGPA/percent/backlogs)
 *              · Selection rounds (workflow picker) · Campuses.
 *   Right sticky "What students see" preview card with tier badge + eligible estimate
 *              + Send-to-campuses CTA.
 */

const BRANCHES = ['B.Tech CSE', 'B.Tech IT', 'B.Tech ECE', 'B.Tech EEE', 'B.Tech MECH', 'MCA', 'M.Tech'];
// SEED_CAMPUSES removed in Step 92 — recruiter's campus picker now reads real
// MoU-linked colleges from `usePartnerColleges` (see below). The previous fake list
// (Godavari / GEC Tadepalligudem / SVEC / GIT Rajahmundry / GIM) shipped a lie: a
// recruiter picking "Godavari" thought their opening went to Godavari even when their
// partner had no MoU with any Godavari-Group college.

interface CtcRow { label: string; amount: number }
interface RoundRow { name: string; mode: 'Online' | 'On campus' | 'Video call' }

export function CompanyOpeningFormPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const editing = Boolean(id);
  const opp = useOpportunity(id);
  const create = useCreateOpportunity();
  const update = useUpdateOpportunity();
  const publish = usePublishOpportunity();

  // All defaults blanked in Gap-fix pass 2 — previous Wipro-shaped example values
  // (Project Engineer / Elite NTH 2026 / 590k fixed pay / 12-month ₹75k bond / 4 branches
  // pre-selected) shipped as a lie whenever a recruiter forgot to overwrite one field.
  // Every recruiter must now type their own opening or edit-from-draft.
  const [title, setTitle] = useState('');
  const [programme, setProgramme] = useState('');
  // F-111 — the type field maps to Opportunity.type, which the server validates against
  // the canonical OPPORTUNITY_TYPES enum. Free-text options ('Full-time', 'Internship + PPO')
  // that used to live here would fail server validation.
  const [type, setType] = useState<OpportunityType>('Placement_OnCampus');
  const [positions, setPositions] = useState(0);
  const [locations, setLocations] = useState('');
  const [description, setDescription] = useState('');
  const [ctc, setCtc] = useState<CtcRow[]>([{ label: 'Fixed pay', amount: 0 }]);
  const [bondMonths, setBondMonths] = useState(0);
  const [bondAmount, setBondAmount] = useState('');
  const [branches, setBranches] = useState<Set<string>>(new Set());
  const [minCGPA, setMinCGPA] = useState('');
  const [class1012, setClass1012] = useState('');
  const [backlogs, setBacklogs] = useState('');
  const [rounds, setRounds] = useState<RoundRow[]>([]);
  // Step 92 — real MoU-linked partner colleges (was SEED_CAMPUSES fake list).
  // Load on mount, keep selection state locally in `campuses` so toggles work.
  const partnerColleges = usePartnerColleges();
  const [campuses, setCampuses] = useState<Array<[string, number, boolean, string]>>([]);
  useEffect(() => {
    const rows = partnerColleges.data?.colleges ?? [];
    if (rows.length === 0) return;
    // Preserve any prior selection by name if the user was mid-edit; else default MoU-active
    // colleges to selected (recruiter's natural expectation — send to who's already a partner).
    setCampuses((prev) => {
      const prevByName = new Map(prev.map((c) => [c[0], c]));
      return rows.map((c): [string, number, boolean, string] => {
        const preserved = prevByName.get(c.name);
        const selected = preserved ? preserved[2] : c.mouActive;
        // The subtitle explains the MoU state to the recruiter.
        const subtitle = c.mouActive
          ? (c.daysToMouExpiry != null && c.daysToMouExpiry <= 60
              ? `MoU expires in ${c.daysToMouExpiry}d`
              : 'Partner (MoU active)')
          : 'No active MoU — proposing coverage';
        return [c.name, c.totalStudents ?? 0, selected, subtitle];
      });
    });
  }, [partnerColleges.data]);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  // v1.6 §7.6 — every Opportunity carries a Workflow template ref. Default to the canonical
  // Kollez Public one when the recruiter hasn't picked yet.
  const [workflowId, setWorkflowId] = useState<string>('');
  const workflowsQ = useWorkflows({ status: 'live' });
  const availableWorkflows = workflowsQ.data?.rows ?? [];
  // v1.6 §7.13 — required + preferred skills. Freeform strings today, but the SkillPicker
  // guides recruiters to canonical SkillTaxonomy.slug values so downstream badge matching
  // and Nexie skill scoring both operate on one vocabulary.
  const [requiredSkills, setRequiredSkills] = useState<string[]>([]);
  const [preferredSkills, setPreferredSkills] = useState<string[]>([]);
  const [pendingRequiredSkill, setPendingRequiredSkill] = useState('');
  const [pendingPreferredSkill, setPendingPreferredSkill] = useState('');

  // Hydrate from server if editing
  useEffect(() => {
    if (!editing || !opp.data?.opportunity) return;
    const o = opp.data.opportunity as unknown as {
      title?: string; type?: string; description?: string; location?: string;
      compensation?: { ctc?: { min?: number } };
      workflow?: string | { _id?: string };
      requiredSkills?: string[];
      preferredSkills?: string[];
    };
    if (o.title) setTitle(o.title);
    // F-111 — only accept known enum values. A stale/free-text `type` from an older row
    // falls through to the current dropdown default rather than silently corrupting state.
    if (o.type && (OPPORTUNITY_TYPES as readonly string[]).includes(o.type)) {
      setType(o.type as OpportunityType);
    }
    if (o.description) setDescription(o.description);
    if (o.location) setLocations(o.location);
    if (o.compensation?.ctc?.min) {
      setCtc([{ label: 'Total CTC', amount: o.compensation.ctc.min }]);
    }
    if (o.workflow) {
      const wfId = typeof o.workflow === 'string' ? o.workflow : o.workflow._id ?? '';
      if (wfId) setWorkflowId(wfId);
    }
    if (Array.isArray(o.requiredSkills)) setRequiredSkills(o.requiredSkills);
    if (Array.isArray(o.preferredSkills)) setPreferredSkills(o.preferredSkills);
  }, [editing, opp.data]);

  // Auto-default to the canonical Kollez Public template when nothing is picked yet.
  useEffect(() => {
    if (workflowId) return;
    if (availableWorkflows.length === 0) return;
    const canonical = availableWorkflows.find((w) => w.scope === 'GLOBAL' && /kollez public/i.test(w.name))
      ?? availableWorkflows.find((w) => w.scope === 'GLOBAL')
      ?? availableWorkflows[0];
    if (canonical) setWorkflowId(canonical._id);
  }, [workflowId, availableWorkflows]);

  const totalCtc = useMemo(() => ctc.reduce((sum, c) => sum + c.amount, 0), [ctc]);
  const tier: 0 | 1 | 2 = totalCtc >= 900000 ? 0 : totalCtc >= 450000 ? 1 : 2;

  // Preview
  const branchFactor = [...branches].length / 4;
  const eligibleEstimate = campuses.filter((c) => c[2]).reduce((s, c) => s + Math.round(c[1] * branchFactor), 0);
  const selectedCampuses = campuses.filter((c) => c[2]).length;
  const cities = locations.split(',').map((s) => s.trim()).filter(Boolean);

  function toggleBranch(b: string) {
    const next = new Set(branches);
    next.has(b) ? next.delete(b) : next.add(b);
    setBranches(next);
    dirty();
  }
  function dirty() {
    setSavedAt('Saving…');
    setTimeout(() => setSavedAt('Draft saved just now'), 700);
  }

  async function saveDraft(): Promise<string | null> {
    const body: Record<string, unknown> = {
      title,
      type,
      description,
      location: cities.join(', '),
      compensation: { ctc: { min: totalCtc } },
      eligibility: {
        branches: [...branches],
        minCGPA: Number(minCGPA) || undefined,
        maxBacklogs: backlogs === 'None' ? 0 : 1,
      },
      requiredSkills,
      preferredSkills,
      scope: 'TENANT',
      ...(workflowId ? { workflow: workflowId } : {}),
    };
    try {
      if (editing && id) {
        await update.mutateAsync({ id, patch: body });
        toast.show({ message: 'Draft saved' });
        return id;
      }
      const res = await create.mutateAsync(body);
      toast.show({ message: 'Draft saved' });
      return res.opportunity._id;
    } catch {
      toast.show({ tone: 'warning', message: 'Save failed' });
      return null;
    }
  }

  async function sendInvites() {
    const savedId = await saveDraft();
    if (!savedId) return;
    try {
      await publish.mutateAsync(savedId);
      // Deep-audit fix (Page 45/N): the old toast said 'Sent to N campuses.
      // Placement officers usually confirm within 2 days.' — but publish
      // only flips Opportunity.status to Active; it doesn't route the
      // opening to a set of colleges (no CampusInvitation is written).
      // The 'usually confirm within 2 days' SLA had no source. Rewrote
      // to say what actually happened.
      toast.show({ message: 'Opening is live. Eligible students at your partner colleges will see it on their Explore page.' });
      nav('/company/openings');
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Could not publish';
      toast.show({ tone: 'warning', message: msg });
    }
  }

  return (
    <AsyncBoundary
      kind="detail"
      loading={editing && opp.isLoading}
      error={editing ? opp.error : undefined}
      empty={editing && !opp.isLoading && !opp.data}
      emptyTitle="Opening not found"
    >
      <div className="stack" style={{ paddingBottom: 96 }}>
        <style>{`
          .op-grid { display: grid; gap: 20px; grid-template-columns: minmax(0,1fr); align-items: start; }
          @media (min-width: 1100px) { .op-grid { grid-template-columns: minmax(0,1fr) 340px; } .op-prev { position: sticky; top: 76px; } }
          .ctc-rows { display: grid; gap: 8px; }
          .ctc-row { display: grid; grid-template-columns: minmax(0,1fr) 150px 36px; gap: 8px; align-items: center; }
          .round { display: grid; grid-template-columns: 28px minmax(0,1fr) 150px 36px; gap: 8px; align-items: center; }
          .round .n { width: 28px; height: 28px; border-radius: 50%; display: grid; place-items: center; background: rgb(var(--bg-tertiary)); font-size: .8125rem; font-weight: 600; }
          .stu-card { border: 1px solid rgb(var(--line-tertiary)); border-radius: 12px; padding: 16px; background: rgb(var(--bg-primary)); display: grid; gap: 10px; }
        `}</style>

        <div className="page-header">
          <div className="ph-text">
            <h1 className="h-page">{editing ? 'Edit opening' : 'New opening'}</h1>
            <p className="subhead">Describe the role once. Every campus you invite gets the same brief, and their placement officers set the drive date.</p>
          </div>
          <div className="ph-actions">
            <span className="helper">{savedAt ?? 'Draft · not saved'}</span>
          </div>
        </div>

        <div className="op-grid">
          <div className="grid gap-5 min-w-0">
            {/* Role */}
            <section className="card card-pad grid gap-4">
              <h2 className="h-section">Role</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="grid gap-1.5">
                  <span className="label">Title</span>
                  <input className="input" value={title} onChange={(e) => { setTitle(e.target.value); dirty(); }} />
                </label>
                <label className="grid gap-1.5">
                  <span className="label">Hiring programme</span>
                  <input className="input" value={programme} onChange={(e) => setProgramme(e.target.value)} />
                </label>
                <label className="grid gap-1.5">
                  <span className="label">Job type</span>
                  <select
                    className="select"
                    value={type}
                    onChange={(e) => setType(e.target.value as OpportunityType)}
                  >
                    {OPPORTUNITY_TYPES.map((t) => (
                      <option key={t} value={t}>{OPPORTUNITY_TYPE_LABELS[t]}</option>
                    ))}
                  </select>
                </label>
                <label className="grid gap-1.5">
                  <span className="label">Positions</span>
                  <input className="input" type="number" value={positions} onChange={(e) => setPositions(Number(e.target.value))} />
                  <span className="helper">Across all campuses</span>
                </label>
                <label className="grid gap-1.5 sm:col-span-2">
                  <span className="label">Locations</span>
                  <input className="input" value={locations} onChange={(e) => { setLocations(e.target.value); dirty(); }} />
                  <span className="helper">Say "assigned at joining" if students can't choose</span>
                </label>
                <label className="grid gap-1.5 sm:col-span-2">
                  <span className="label">What they'll do</span>
                  <textarea className="textarea" rows={4} value={description} onChange={(e) => { setDescription(e.target.value); dirty(); }} />
                  <span className="helper">Students read this on their phones. Keep it under 80 words.</span>
                </label>
              </div>
            </section>

            {/* Pay */}
            <section className="card card-pad grid gap-4">
              <div className="flex items-baseline justify-between">
                <h2 className="h-section">Pay</h2>
                <span className="text-sm">Total CTC <b>₹{totalCtc.toLocaleString('en-IN')}</b></span>
              </div>
              <div className="ctc-rows">
                {ctc.map((c, i) => (
                  <div key={i} className="ctc-row">
                    <input
                      className="input"
                      value={c.label}
                      onChange={(e) => { const next = [...ctc]; next[i]!.label = e.target.value; setCtc(next); dirty(); }}
                      aria-label="Component name"
                    />
                    <div className="input-group">
                      <span className="input-addon">₹</span>
                      <input
                        className="input"
                        inputMode="numeric"
                        value={c.amount.toLocaleString('en-IN')}
                        onChange={(e) => { const next = [...ctc]; next[i]!.amount = Number(e.target.value.replace(/[^\d]/g, '')) || 0; setCtc(next); dirty(); }}
                        aria-label="Amount"
                      />
                    </div>
                    <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setCtc(ctc.filter((_, x) => x !== i))} aria-label="Remove">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button className="btn btn-ghost btn-sm justify-self-start" onClick={() => setCtc([...ctc, { label: 'Relocation allowance', amount: 0 }])}>
                <Plus size={14} />Add a component
              </button>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="grid gap-1.5">
                  <span className="label">Service agreement</span>
                  <select className="select" value={bondMonths} onChange={(e) => setBondMonths(Number(e.target.value))}>
                    <option value={12}>12 months</option>
                    <option value={0}>None</option>
                    <option value={24}>24 months</option>
                  </select>
                </label>
                <label className="grid gap-1.5">
                  <span className="label">Amount if they leave early</span>
                  <div className="input-group">
                    <span className="input-addon">₹</span>
                    <input className="input" value={bondAmount} onChange={(e) => setBondAmount(e.target.value)} disabled={bondMonths === 0} />
                  </div>
                </label>
              </div>
              <p className="helper m-0">At {formatLPA(totalCtc)} most colleges will list this as a <b>Day {tier}</b> drive.</p>
            </section>

            {/* Who can apply */}
            <section className="card card-pad grid gap-4">
              <h2 className="h-section">Who can apply</h2>
              <div className="grid gap-1.5">
                <span className="label">Degrees and branches</span>
                <div className="flex flex-wrap gap-2">
                  {BRANCHES.map((b) => (
                    <button key={b} className="tag" aria-pressed={branches.has(b)} onClick={() => toggleBranch(b)}>{b}</button>
                  ))}
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <label className="grid gap-1.5">
                  <span className="label">Minimum CGPA</span>
                  <input className="input" value={minCGPA} onChange={(e) => setMinCGPA(e.target.value)} />
                </label>
                <label className="grid gap-1.5">
                  <span className="label">10th / 12th</span>
                  <input className="input" value={class1012} onChange={(e) => setClass1012(e.target.value)} />
                </label>
                <label className="grid gap-1.5">
                  <span className="label">Active backlogs</span>
                  <select className="select" value={backlogs} onChange={(e) => setBacklogs(e.target.value)}>
                    <option>None</option>
                    <option>Up to 1</option>
                  </select>
                </label>
              </div>
              <div className="notice notice-info">
                <Info size={14} />
                <div>Colleges apply their own placement policy on top (for example, keeping placed students out). You'll see the final eligible count once they confirm.</div>
              </div>
            </section>

            {/* Skills — v1.6 §7.13 canonical taxonomy */}
            <section className="card card-pad grid gap-3">
              <h2 className="h-section">Skills</h2>
              <p className="helper m-0">
                Pick canonical skills so student badges and Nexie matching align. Aliases resolve
                to slugs automatically. Unknown skills work but Nexie can't fully score them.
              </p>

              <div className="grid gap-2">
                <span className="label">Required — students must have these</span>
                <div className="flex flex-wrap gap-1">
                  {requiredSkills.length === 0 ? (
                    <span className="cell-sub"><em>none yet — add the ones this role really needs</em></span>
                  ) : requiredSkills.map((s) => (
                    <span key={s} className="tag tag-pressed" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {s}
                      <button
                        type="button"
                        className="btn-icon-mini"
                        aria-label={`Remove ${s}`}
                        onClick={() => { setRequiredSkills(requiredSkills.filter((x) => x !== s)); dirty(); }}
                      >
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="grid gap-1" style={{ maxWidth: 320 }}>
                  <SkillPicker
                    value={pendingRequiredSkill}
                    onChange={(v) => setPendingRequiredSkill(v)}
                    placeholder="Add a required skill…"
                    listId="opening-required-skill"
                  />
                </div>
                <div>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={!pendingRequiredSkill.trim() || requiredSkills.includes(pendingRequiredSkill.trim())}
                    onClick={() => {
                      const v = pendingRequiredSkill.trim();
                      if (!v) return;
                      setRequiredSkills([...requiredSkills, v]);
                      setPendingRequiredSkill('');
                      dirty();
                    }}
                  >
                    <Plus size={14} />
                    Add
                  </button>
                </div>
              </div>

              <div className="grid gap-2">
                <span className="label">Preferred — nice to have, boost match score</span>
                <div className="flex flex-wrap gap-1">
                  {preferredSkills.length === 0 ? (
                    <span className="cell-sub"><em>none</em></span>
                  ) : preferredSkills.map((s) => (
                    <span key={s} className="tag" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {s}
                      <button
                        type="button"
                        className="btn-icon-mini"
                        aria-label={`Remove ${s}`}
                        onClick={() => { setPreferredSkills(preferredSkills.filter((x) => x !== s)); dirty(); }}
                      >
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="grid gap-1" style={{ maxWidth: 320 }}>
                  <SkillPicker
                    value={pendingPreferredSkill}
                    onChange={(v) => setPendingPreferredSkill(v)}
                    placeholder="Add a preferred skill…"
                    listId="opening-preferred-skill"
                  />
                </div>
                <div>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={!pendingPreferredSkill.trim() || preferredSkills.includes(pendingPreferredSkill.trim())}
                    onClick={() => {
                      const v = pendingPreferredSkill.trim();
                      if (!v) return;
                      setPreferredSkills([...preferredSkills, v]);
                      setPendingPreferredSkill('');
                      dirty();
                    }}
                  >
                    <Plus size={14} />
                    Add
                  </button>
                </div>
              </div>
            </section>

            {/* Rounds */}
            <section className="card card-pad grid gap-3">
              <h2 className="h-section">Selection rounds</h2>
              <label className="grid gap-1.5">
                <span className="label">Workflow template</span>
                <select
                  className="select"
                  value={workflowId}
                  onChange={(e) => { setWorkflowId(e.target.value); dirty(); }}
                >
                  <option value="">— none (Application will save without a run) —</option>
                  {availableWorkflows.map((w) => (
                    <option key={w._id} value={w._id}>
                      {w.name} · v{w.version} · {w.scope} · {w.stages.length} stages
                    </option>
                  ))}
                </select>
                <span className="helper">
                  Every Application on this opening will run through this template's stages. The canonical
                  Kollez Public one is picked by default — swap in your own from{' '}
                  <a className="link" href="/workflows" target="_blank" rel="noreferrer">/workflows</a>.
                </span>
              </label>
              <div className="grid gap-2">
                {rounds.map((r, i) => (
                  <div key={i} className="round">
                    <span className="n">{i + 1}</span>
                    <input
                      className="input"
                      value={r.name}
                      onChange={(e) => { const next = [...rounds]; next[i]!.name = e.target.value; setRounds(next); }}
                      aria-label={`Round ${i + 1}`}
                    />
                    <select
                      className="select"
                      value={r.mode}
                      onChange={(e) => { const next = [...rounds]; next[i]!.mode = e.target.value as RoundRow['mode']; setRounds(next); }}
                    >
                      <option>Online</option>
                      <option>On campus</option>
                      <option>Video call</option>
                    </select>
                    <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setRounds(rounds.filter((_, x) => x !== i))} aria-label="Remove round">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button className="btn btn-ghost btn-sm justify-self-start" onClick={() => setRounds([...rounds, { name: 'Group discussion', mode: 'On campus' }])}>
                <Plus size={14} />Add a round
              </button>
              <div className="notice notice-info">
                <Clock size={14} />
                <div>The median on kollez is 11 days from application to offer — campuses rank companies partly on how fast results come back.</div>
              </div>
            </section>

            {/* Campuses */}
            <section className="card card-pad grid gap-3">
              <h2 className="h-section">Campuses</h2>
              <div className="grid gap-2">
                {campuses.map((c, i) => {
                  const disabled = /Already|No matching/.test(c[3]);
                  return (
                    <label key={c[0]} className={`flex items-center gap-3 py-2 border-b border-line-tertiary last:border-0 ${disabled ? 'opacity-60' : ''}`}>
                      <input
                        type="checkbox"
                        className="check"
                        checked={c[2]}
                        disabled={disabled}
                        onChange={(e) => {
                          const next = [...campuses];
                          next[i]![2] = e.target.checked;
                          setCampuses(next);
                          dirty();
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="cell-title">{c[0]}</div>
                        <div className="cell-sub">{c[3]}</div>
                      </div>
                      <span className="cell-sub whitespace-nowrap">≈ {Math.round(c[1] * branchFactor).toLocaleString('en-IN')}</span>
                    </label>
                  );
                })}
              </div>
            </section>
          </div>

          <aside className="op-prev grid gap-3" aria-label="Student preview">
            <p className="eyebrow">What students will see</p>
            <div className="stu-card">
              <div className="flex items-center gap-3">
                <span className="logo-tile">CO</span>
                <div className="min-w-0">
                  <div className="cell-title">Your company</div>
                  <div className="text-sm text-ink-secondary">{title || 'Untitled role'}</div>
                </div>
                <span className="badge badge-success ml-auto">Eligible</span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-secondary">
                <b className="text-ink-primary">{formatLPA(totalCtc)}</b>
                <span>{cities.length > 1 ? `${cities.length} cities` : cities.join(', ') || 'Location TBD'}</span>
                <span className={`tier${tier === 0 ? ' tier-0' : ''}`}>Day {tier}</span>
              </div>
              <div className="cell-sub">{rounds.map((r) => r.name).join(' → ')}</div>
              <div className="cell-sub">
                {bondMonths > 0 ? `${bondMonths}-month service agreement · ₹${bondAmount} if you leave early` : 'No service agreement'}
              </div>
            </div>
            <div className="card card-pad grid gap-2">
              <div className="flex justify-between text-sm">
                <span>Campuses invited</span>
                <b>{selectedCampuses}</b>
              </div>
              <div className="flex justify-between text-sm">
                <span>Students likely eligible</span>
                <b>≈ {eligibleEstimate.toLocaleString('en-IN')}</b>
              </div>
              <button
                className="btn btn-primary mt-2"
                onClick={sendInvites}
                disabled={selectedCampuses === 0 || create.isPending || publish.isPending}
              >
                <Send size={14} />Send to {selectedCampuses} campuses
              </button>
              <button className="btn btn-ghost" onClick={saveDraft} disabled={update.isPending || create.isPending}>
                Save draft
              </button>
            </div>
          </aside>
        </div>
      </div>
    </AsyncBoundary>
  );
}
